/*
 * RutinasMefir.jsx — Aplicaciones terapéuticas del Canal · INSTITUTO MEFIR
 * ------------------------------------------------------------------------
 * Port React NATIVO (Opción A, sin iframe) del prototipo autocontenido
 * `rutinas-mefir-PROTOTIPO.html`. Todos los valores (tiempos, colores, Hz de
 * los pitidos, patrones de vibración, textos de voz, avisos, disclaimers) son
 * COPIA EXACTA del prototipo — fuente de verdad. NO inventar valores.
 *
 * 🔴🔴 SALUD / LEGAL — ESTOS TEXTOS REQUIEREN VALIDACIÓN DE LA FISIOTERAPEUTA
 *      ANTES DE PUBLICAR. Los textos médicos y disclaimers (Kegel, 4-7-8,
 *      suspiro fisiológico, coherencia cardíaca, avisos que rotan, pies de
 *      página) están tomados literalmente del prototipo. No sustituyen consejo
 *      médico. Antes de que esto sea visible para clientas reales, la
 *      fisioterapeuta del Instituto MEFIR debe revisar y aprobar cada texto.
 *
 * Estado en memoria (useState + refs), sin localStorage. Un solo setInterval de
 * 1000 ms, limpiado en pausa y en el desmontaje. Voz (Web Speech API, es-ES,
 * voz local preferida, desbloqueo al primer toque, keep-alive cada 5 s),
 * pitidos (Web Audio), vibración (navigator.vibrate) y wakeLock (re-solicitado
 * al volver a primer plano). Marca: INSTITUTO MEFIR (nunca "Método Pilar
 * Domínguez" en este módulo).
 */
import React, { useState, useRef, useEffect } from 'react';
// Logo MEFIR retirado del selector (Ann 2026-07-23): reiterativo dentro del canal.

/* Sentencia legal AÑADIDA (encargo) además de los textos exactos del prototipo. */
const MED_DISCLAIMER = 'No sustituye consejo médico.';

const CIRC = 289.03;
const NUM_WORDS = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez',
  'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis'];

/* Iconos de línea (mismo estilo que la versión pública /regalo): pelvis, pulmones, corazón. */
const ICO = {
  pelvis: (
    <svg viewBox="0 0 64 64" fill="none" stroke="#4db6bd" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M25 7 C 22 17, 16.5 26, 17.5 37 C 18 44, 22 51, 26 57" />
      <path d="M39 7 C 42 17, 47.5 26, 46.5 37 C 46 44, 42 51, 38 57" />
      <path d="M32 36 C 28.5 31, 21 32.5, 21.5 39.5 C 22 45, 28.5 49, 32 52.5 C 35.5 49, 42 45, 42.5 39.5 C 43 32.5, 35.5 31, 32 36 Z" />
      <path d="M32 41 C 30.2 38.7, 26.6 39.7, 27 42.6 C 27.2 45.2, 30.2 47.2, 32 49 C 33.8 47.2, 36.8 45.2, 37 42.6 C 37.4 39.7, 33.8 38.7, 32 41 Z" />
    </svg>
  ),
  lungs: (
    <svg viewBox="0 0 64 64" fill="none" stroke="#4db6bd" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M32 8 V 21" />
      <path d="M32 21 C 31 23.5, 29 24.5, 27.5 25.5" />
      <path d="M32 21 C 33 23.5, 35 24.5, 36.5 25.5" />
      <path d="M28 26 C 22 27.5, 16.5 31, 15 38 C 13.8 44, 15 51, 20.5 53 C 25 54.5, 29 51.5, 29.3 46 C 29.6 40, 29.4 32, 28.5 26.5 Z" />
      <path d="M36 26 C 42 27.5, 47.5 31, 49 38 C 50.2 44, 49 51, 43.5 53 C 39 54.5, 35 51.5, 34.7 46 C 34.4 40, 34.6 32, 35.5 26.5 Z" />
    </svg>
  ),
  heart: (
    <svg viewBox="0 0 64 64" fill="none" stroke="#4db6bd" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M32 53 C 12 40, 9 23, 20.5 16.5 C 27 12.8, 32 17.5, 32 22 C 32 17.5, 37 12.8, 43.5 16.5 C 55 23, 52 40, 32 53 Z" />
      <path d="M13 35 H24 L27.5 35 L30.5 27 L34 45 L37 30.5 L39.5 35 H51" />
    </svg>
  ),
  boxb: (
    <svg viewBox="0 0 64 64" fill="none" stroke="#4db6bd" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="14" y="14" width="36" height="36" rx="9" />
      <circle cx="14" cy="14" r="4" />
    </svg>
  ),
  body: (
    <svg viewBox="0 0 64 64" fill="none" stroke="#4db6bd" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="32" cy="12" r="6" />
      <path d="M32 20 V 38" />
      <path d="M18 26 C 24 22.5, 40 22.5, 46 26" />
      <path d="M18 26 L 15 40" />
      <path d="M46 26 L 49 40" />
      <path d="M32 38 L 24 56" />
      <path d="M32 38 L 40 56" />
    </svg>
  ),
};

/* Mismos iconos en texto (para los títulos de las fichas "?", que van por HTML). */
const _svg = (inner, sw) => `<svg viewBox="0 0 64 64" width="22" height="22" style="vertical-align:-4px;margin-right:5px" fill="none" stroke="#4db6bd" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
const ICO_STR = {
  pelvis: _svg('<path d="M25 7 C 22 17, 16.5 26, 17.5 37 C 18 44, 22 51, 26 57"/><path d="M39 7 C 42 17, 47.5 26, 46.5 37 C 46 44, 42 51, 38 57"/><path d="M32 36 C 28.5 31, 21 32.5, 21.5 39.5 C 22 45, 28.5 49, 32 52.5 C 35.5 49, 42 45, 42.5 39.5 C 43 32.5, 35.5 31, 32 36 Z"/><path d="M32 41 C 30.2 38.7, 26.6 39.7, 27 42.6 C 27.2 45.2, 30.2 47.2, 32 49 C 33.8 47.2, 36.8 45.2, 37 42.6 C 37.4 39.7, 33.8 38.7, 32 41 Z"/>', 2.6),
  lungs: _svg('<path d="M32 8 V 21"/><path d="M32 21 C 31 23.5, 29 24.5, 27.5 25.5"/><path d="M32 21 C 33 23.5, 35 24.5, 36.5 25.5"/><path d="M28 26 C 22 27.5, 16.5 31, 15 38 C 13.8 44, 15 51, 20.5 53 C 25 54.5, 29 51.5, 29.3 46 C 29.6 40, 29.4 32, 28.5 26.5 Z"/><path d="M36 26 C 42 27.5, 47.5 31, 49 38 C 50.2 44, 49 51, 43.5 53 C 39 54.5, 35 51.5, 34.7 46 C 34.4 40, 34.6 32, 35.5 26.5 Z"/>', 2.4),
  heart: _svg('<path d="M32 53 C 12 40, 9 23, 20.5 16.5 C 27 12.8, 32 17.5, 32 22 C 32 17.5, 37 12.8, 43.5 16.5 C 55 23, 52 40, 32 53 Z"/><path d="M13 35 H24 L27.5 35 L30.5 27 L34 45 L37 30.5 L39.5 35 H51"/>', 2.4),
  boxb: _svg('<rect x="14" y="14" width="36" height="36" rx="9"/><circle cx="14" cy="14" r="4"/>', 2.4),
  body: _svg('<circle cx="32" cy="12" r="6"/><path d="M32 20 V 38"/><path d="M18 26 C 24 22.5, 40 22.5, 46 26"/><path d="M18 26 L 15 40"/><path d="M46 26 L 49 40"/><path d="M32 38 L 24 56"/><path d="M32 38 L 40 56"/>', 2.4),
};

/* ===== Fichas informativas (botón "?") — texto EXACTO del prototipo ===== */
const INFO = {
  kegel: {
    title: ICO_STR.pelvis + 'Kegel · Suelo pélvico',
    body: '<span style="display:block;color:var(--mefir2);font-weight:700;margin-bottom:12px">«Aprieta, eleva y suelta: recupera el control de tu suelo pélvico.»</span><b>Qué es:</b> contraer y relajar el suelo pélvico de forma controlada para darle tono y control.<br><br><b>Para qué:</b> incontinencia urinaria, postparto y fortalecer el suelo pélvico.<br><br><b>Evidencia:</b> tratamiento de primera línea, médicamente reconocido, para la incontinencia.<br><br><small>Dr. Arnold Kegel, ginecólogo · Instituto MEFIR · No sustituye consejo médico.</small>',
  },
  breath: {
    title: ICO_STR.lungs + 'Respiración 4-7-8',
    body: '<span style="display:block;color:var(--mefir2);font-weight:700;margin-bottom:12px">«Inhala, retén y suelta largo: la respiración que te lleva al sueño.»</span><b>Qué es:</b> inhalar 4, sostener 7 y exhalar 8, alargando la salida del aire.<br><br><b>Para qué:</b> calmar la ansiedad y ayudar a conciliar el sueño.<br><br><b>Evidencia:</b> respiración lenta, muy popular; efecto relajante en pocos minutos.<br><br><small>Dr. Andrew Weil, Universidad de Harvard · Instituto MEFIR · No sustituye consejo médico.</small>',
  },
  sigh: {
    title: ICO_STR.lungs + 'Suspiro fisiológico',
    body: '<span style="display:block;color:var(--mefir2);font-weight:700;margin-bottom:12px">«Dos inhalaciones y una exhalación larga: calma el estrés en un minuto.»</span><b>Qué es:</b> dos inhalaciones seguidas y una exhalación larga.<br><br><b>Para qué:</b> bajar el estrés en 1-2 minutos, en cualquier momento de agobio.<br><br><b>Evidencia:</b> con base científica reciente; de las formas más rápidas de calmarse.<br><br><small>Dr. Andrew Huberman, Universidad de Stanford · Instituto MEFIR · No sustituye consejo médico.</small>',
  },
  /* 🔴 SALUD/LEGAL: textos PENDIENTES DE VALIDACIÓN por la fisioterapeuta (encargo Ann 2026-07-26). */
  box: {
    title: ICO_STR.boxb + 'Respiración cuadrada',
    body: '<span style="display:block;color:var(--mefir2);font-weight:700;margin-bottom:12px">«Inhala, sostén, exhala, sostén: calma y concentración.»</span><b>Qué es:</b> cuatro tiempos iguales — inhalar 4, sostener 4, exhalar 4, sostener 4 — como un cuadrado.<br><br><b>Para qué:</b> calmar el estrés y mejorar la concentración al instante.<br><br><b>Evidencia:</b> respiración lenta; usada por los Navy SEALs y en mindfulness. Base científica moderada.<br><br><small>Tradición yóguica, popularizada por el excomandante Navy SEAL Mark Divine · Instituto MEFIR · No sustituye consejo médico. Si te mareas, para y respira normal.</small>',
  },
  jacobson: {
    title: ICO_STR.body + 'Relajación muscular progresiva',
    body: '<span style="display:block;color:var(--mefir2);font-weight:700;margin-bottom:12px">«Tensa y suelta cada músculo hasta liberar la tensión.»</span><b>Qué es:</b> tensar y soltar los músculos por grupos, de la cabeza a los pies, para liberar la tensión.<br><br><b>Para qué:</b> estrés, ansiedad, insomnio, cefalea tensional y dolor cervical o lumbar por tensión.<br><br><b>Evidencia:</b> de las técnicas de relajación más estudiadas y con más respaldo. Segura y sencilla.<br><br><small>Dr. Edmund Jacobson · Instituto MEFIR · No sustituye consejo médico.</small>',
  },
  coherence: {
    title: ICO_STR.heart + 'Coherencia cardíaca',
    body: '<span style="display:block;color:var(--mefir2);font-weight:700;margin-bottom:12px">«Respira a seis por minuto y calma tu corazón y tu mente.»</span><b>Qué es:</b> respirar a 6 por minuto (inhalar 5s, exhalar 5s), sin retener.<br><br><b>Para qué:</b> equilibrar el sistema nervioso, la tensión arterial y la ansiedad.<br><br><b>Evidencia:</b> muy usada en cardiología y psicología por sus resultados.<br><br><small>Dr. David O’Hare · Instituto MEFIR · No sustituye consejo médico.</small>',
  },
};

/* ===== Motor KEGEL — valores EXACTOS del prototipo ===== */
const PARTS = [
  { name: 'Parte 1 · Lentas suaves', intro: 'Parte uno: contracciones lentas de tres segundos.', type: 'slow', contract: 3, relax: 6, reps: 8 },
  { name: 'Parte 2 · Lentas largas', intro: 'Parte dos: contracciones más largas, de seis segundos.', type: 'slow', contract: 6, relax: 6, reps: 8 },
  { name: 'Parte 3 · Rápidas', intro: 'Parte tres: rápidas. Contrae y relaja cada segundo.', type: 'quick', quick: 12 },
];
const REST_BETWEEN = 20;
const TIPS = [
  { v: '¡Ojo! No aguantes la respiración. Nada de apneas: respira con normalidad.', t: '⚠️ ¡Ojo! No aguantes la respiración' },
  { v: '¡Ojo! No aprietes los glúteos ni el abdomen. Solo el suelo pélvico.', t: '⚠️ No actives glúteos ni abdomen, solo el suelo pélvico' },
  { v: 'No empujes hacia abajo. Cierra y eleva los orificios hacia dentro.', t: '⚠️ No empujes hacia abajo: cierra y eleva hacia dentro' },
  { v: 'No aprietes los muslos ni los aductores. Trabájalo de forma aislada.', t: '⚠️ No aprietes muslos ni aductores' },
  { v: 'Contrae a intensidad media, submáxima. No hace falta el máximo.', t: '⚠️ Intensidad media, no al máximo' },
  { v: 'Mantén la columna recta y la pelvis neutra. Hombros y mandíbula sueltos.', t: '⚠️ Columna recta, pelvis neutra, hombros sueltos' },
  { v: 'Recuerda: lo importante no es hacer muchas, sino hacerlas bien.', t: '⚠️ Pocas y bien hechas, mejor que muchas mal' },
  { v: '¡Ojo! No hagas demasiada fuerza. Relaja del todo entre cada repetición.', t: '⚠️ No fuerces: relaja por completo entre repeticiones' },
  { v: 'No los hagas siempre tumbada. Ve progresando: sentada, a cuatro patas y de pie.', t: '⚠️ No solo tumbada: progresa a sentada y de pie' },
  { v: 'Y sobre todo, complementa con los ejercicios del Instituto MEFIR que refuerzan tu suelo pélvico.', t: '💚 Complétalo con los ejercicios del Método MEFIR' },
  { v: 'La calidad es siempre mejor que la cantidad. Concéntrate, no lo hagas por hacer.', t: '💚 Calidad, no cantidad: hazlo concentrada' },
];

/* ===== Motor RESPIRACIONES (4-7-8, suspiro, coherencia) — EXACTO del prototipo ===== */
const BREATHS = {
  breath: {
    name: 'Respiración 4-7-8', sub: 'técnica del Dr. Andrew Weil', unit: 'rondas', defaultRounds: 4,
    intro: 'Respiración cuatro, siete, ocho, del Instituto MEFIR. Ponte cómoda, con la lengua tras los dientes de arriba. Inhalarás cuatro, sostendrás siete y exhalarás ocho. Uno',
    prepCue: '🧘 Siéntate cómoda, lengua tras los dientes de arriba',
    rounds: [{ lab: '4 rondas', sub: 'rápido', val: 4 }, { lab: '8 rondas', sub: 'completo', val: 8 }],
    doneVoice: 'Respiración cuatro, siete, ocho completada. Muy bien. Practícala dos veces al día para notar sus efectos.',
    doneMsg: '✨ Respiración completada 🌙<br><span style="font-weight:600;font-size:.85rem;color:var(--muted)">Practícala 2 veces al día · Instituto MEFIR</span>',
    footer: 'Inhala por la nariz 4s, sostén 7s, exhala por la boca 8s (soplando suave). Empieza con 4 rondas. Si te mareas, para y respira normal. No la hagas conduciendo. Técnica creada por el <b>Dr. Andrew Weil</b>, médico de la Universidad de Harvard.<br><br><span class="brand">INSTITUTO MEFIR</span>',
    seq: [
      { label: 'INHALA', sub: 'por la nariz, 4 s', cls: 'r', dur: 4, scale: 1.25, word: 'inhala', voice: 'Inhala', cue: '🌬️ Inhala en silencio por la nariz', beep: 520, vib: 60 },
      { label: 'SOSTÉN', sub: 'retén el aire, 7 s', cls: 'h', dur: 7, scale: 1.25, word: 'sostén', voice: 'Sostén', cue: '⏸️ Retén suave, sin tensarte', beep: 660, vib: 90 },
      { label: 'EXHALA', sub: 'sopla por la boca, 8 s', cls: 'g', dur: 8, scale: 0.7, word: 'exhala', voice: 'Exhala, sopla despacio', cue: '💨 Sopla suave y largo por la boca', beep: 392, vib: 40, tip: true },
    ],
    tips: [
      'Inhala en silencio por la nariz.',
      'Al exhalar, sopla suave por la boca, como si soplaras una vela sin apagarla.',
      'Mantén la lengua tocando detrás de los dientes de arriba, como enseñaba el Dr. Weil.',
      'Hombros y mandíbula relajados. Deja que el cuerpo se afloje en cada exhalación.',
    ],
  },
  sigh: {
    name: 'Suspiro fisiológico', sub: 'técnica del Dr. Andrew Huberman', unit: 'ciclos', defaultRounds: 3,
    intro: 'Suspiro fisiológico, del Instituto MEFIR. Harás una inhalación por la nariz, una segunda más corta, y una exhalación larga por la boca. Uno',
    prepCue: '🧘 Ponte cómoda. Vas a calmarte en un minuto',
    rounds: [{ lab: '3 ciclos', sub: 'reset rápido', val: 3 }, { lab: '6 ciclos', sub: 'calma profunda', val: 6 }],
    doneVoice: 'Suspiro fisiológico completado. Nota cómo baja la tensión. Puedes repetirlo cuando lo necesites.',
    doneMsg: '✨ Suspiro completado 🌿<br><span style="font-weight:600;font-size:.85rem;color:var(--muted)">Úsalo en cualquier momento de estrés · Instituto MEFIR</span>',
    footer: 'Inhala por la nariz, haz una segunda inhalación corta para acabar de llenar los pulmones, y suelta el aire largo por la boca. Baja el estrés en 1-2 minutos. Técnica divulgada por el <b>Dr. Andrew Huberman</b>, neurocientífico de Stanford.<br><br><span class="brand">INSTITUTO MEFIR</span>',
    seq: [
      { label: 'INHALA', sub: 'por la nariz, llena los pulmones', cls: 'r', dur: 3, scale: 1.12, word: 'inhala', voice: 'Inhala por la nariz', cue: '🌬️ Inhala llenando los pulmones', beep: 500, vib: 60 },
      { label: 'UN POCO MÁS', sub: 'segunda inhalación corta', cls: 'r', dur: 1, scale: 1.30, word: 'más aire', voice: 'Y un poco más', cue: '⬆️ Segunda inhalación corta, hasta arriba', beep: 640, vib: 40 },
      { label: 'EXHALA', sub: 'suelta largo por la boca', cls: 'g', dur: 6, scale: 0.68, word: 'exhala', voice: 'Exhala largo por la boca', cue: '💨 Suelta despacio y del todo', beep: 392, vib: 40, tip: true },
    ],
    tips: [
      'La clave es la exhalación larga: ahí se calma el cuerpo.',
      'La segunda inhalación reabre los pulmones y ayuda a soltar más aire.',
      'Hombros sueltos. Deja caer la tensión con cada salida de aire.',
      'Con uno a tres suspiros ya notas el efecto; el resto es calma extra.',
    ],
  },
  box: {
    name: 'Respiración cuadrada', sub: 'box breathing · 4-4-4-4', unit: 'rondas', defaultRounds: 4,
    intro: 'Respiración cuadrada, del Instituto MEFIR. Cuatro tiempos iguales: inhala, sostén, exhala y sostén. Si te mareas, para. Uno',
    prepCue: '🧘 Siéntate con la espalda recta y respira con calma',
    rounds: [{ lab: '4 rondas', sub: '~1 min', val: 4 }, { lab: '8 rondas', sub: '~2 min', val: 8 }],
    doneVoice: 'Sesión completada. Tu mente está más en calma. Úsala cuando necesites concentrarte.',
    doneMsg: '✨ Sesión completada. Mente en calma.<br><span style="font-weight:600;font-size:.85rem;color:var(--muted)">Úsala cuando necesites concentrarte · Instituto MEFIR</span>',
    footer: 'Inhala 4 segundos, sostén 4, exhala 4 y sostén 4 en vacío, como un cuadrado. Calma el estrés y mejora la concentración. Si te mareas, para y respira normal; no recomendada en embarazo ni si te marean las apneas (usa la coherencia cardíaca). Tradición yóguica, popularizada por el excomandante Navy SEAL <b>Mark Divine</b>.<br><br><span class="brand">INSTITUTO MEFIR</span>',
    seq: [
      { label: 'INHALA', sub: 'por la nariz, 4 s', cls: 'r', dur: 4, scale: 1.25, word: 'inhala', voice: 'Inhala', cue: 'Inhala por la nariz, llenando', beep: 520, vib: 70, dot: { top: '6%', left: '94%' } },
      { label: 'SOSTÉN', sub: 'con aire, 4 s', cls: 'h', dur: 4, scale: 1.25, word: 'sostén', voice: 'Sostén', cue: 'Mantén el aire, sin tensarte', beep: 660, vib: 40, dot: { top: '94%', left: '94%' } },
      { label: 'EXHALA', sub: 'por la boca, 4 s', cls: 'g', dur: 4, scale: 0.7, word: 'exhala', voice: 'Exhala', cue: 'Exhala despacio por la boca', beep: 392, vib: 50, dot: { top: '94%', left: '6%' } },
      { label: 'SOSTÉN', sub: 'vacío, 4 s', cls: 'h', dur: 4, scale: 0.7, word: 'vacío', voice: 'Mantén, vacío', cue: 'Mantén vacío, tranquila', beep: 660, vib: 40, dot: { top: '6%', left: '6%' } },
    ],
    tips: [],
  },
  coherence: {
    name: 'Coherencia cardíaca', sub: 'método del Dr. David O’Hare', unit: 'respiraciones', defaultRounds: 18,
    intro: 'Coherencia cardíaca, del Instituto MEFIR. Inhala cinco segundos y exhala cinco segundos, a un ritmo suave y constante, sin retener el aire. Uno',
    prepCue: '🧘 Espalda recta, respira natural. Ritmo suave y constante',
    rounds: [{ lab: '3 min', sub: '18 respiraciones', val: 18 }, { lab: '5 min', sub: '30 respiraciones', val: 30 }],
    doneVoice: 'Coherencia cardíaca completada. Tu ritmo cardíaco y tu mente están más en calma. Lo ideal es hacerla tres veces al día.',
    doneMsg: '✨ Coherencia completada 💚<br><span style="font-weight:600;font-size:.85rem;color:var(--muted)">3 veces al día para notar sus efectos · Instituto MEFIR</span>',
    footer: 'Inhala 5 segundos y exhala 5 segundos, sin retener, a seis respiraciones por minuto. Ayuda a equilibrar el sistema nervioso, la tensión arterial y la ansiedad. Método popularizado por el <b>Dr. David O’Hare</b>.<br><br><span class="brand">INSTITUTO MEFIR</span>',
    seq: [
      { label: 'INHALA', sub: 'suave, 5 s', cls: 'r', dur: 5, scale: 1.25, word: 'inhala', voice: 'Inhala', cue: '🌬️ Inhala suave por la nariz', beep: 520, vib: 50 },
      { label: 'EXHALA', sub: 'suave, 5 s', cls: 'g', dur: 5, scale: 0.7, word: 'exhala', voice: 'Exhala', cue: '💨 Exhala suave y largo', beep: 392, vib: 40 },
    ],
    tips: [
      'Sin retener el aire: inhala y exhala de forma continua.',
      'Busca un ritmo suave, como una ola que sube y baja.',
      'Deja los hombros sueltos y la cara relajada.',
    ],
  },
};

/* ===== Motor JACOBSON (relajación muscular progresiva) — EXACTO del prototipo
   bloque-relajacion-jacobson.html (Ann 2026-07-26). SOLO en la versión de
   suscripción (este módulo del canal); NO va en el /regalo gratuito. ===== */
const J_GROUPS = [
  { name: 'Manos y puños', cue: 'Cierra los puños con fuerza', reg: 'hands' },
  { name: 'Antebrazos y brazos', cue: 'Dobla los codos y tensa los brazos', reg: 'arms' },
  { name: 'Hombros', cue: 'Sube los hombros hacia las orejas', reg: 'shoulders' },
  { name: 'Cuello y nuca', cue: 'Lleva suavemente la barbilla al pecho', reg: 'neck' },
  { name: 'Frente', cue: 'Arruga la frente subiendo las cejas', reg: 'head' },
  { name: 'Ojos y mejillas', cue: 'Cierra los ojos con suavidad pero firme', reg: 'head' },
  { name: 'Mandíbula', cue: 'Aprieta los dientes suavemente', reg: 'head' },
  { name: 'Pecho', cue: 'Toma aire y tensa el pecho', reg: 'chest' },
  { name: 'Abdomen', cue: 'Tensa el abdomen como un escudo', reg: 'abdomen' },
  { name: 'Espalda baja', cue: 'Arquea muy suavemente la espalda', reg: 'lowback' },
  { name: 'Glúteos', cue: 'Aprieta los glúteos', reg: 'glutes' },
  { name: 'Muslos', cue: 'Tensa los muslos', reg: 'thighs' },
  { name: 'Pantorrillas', cue: 'Lleva las puntas de los pies hacia ti', reg: 'calves' },
  { name: 'Pies', cue: 'Curva los dedos de los pies', reg: 'feet' },
];
const J_CORTA_IDX = [0, 2, 3, 6, 8, 10, 11, 13]; // 8 grupos representativos
const J_TENSE = 5, J_RELAX = 12, J_PREP = 3;
const J_TIPS = [
  { t: '⚠️ No aguantes la respiración: respira con normalidad', v: 'No aguantes la respiración, respira con normalidad.' },
  { t: '⚠️ Tensa con firmeza pero sin llegar al dolor', v: 'Tensa con firmeza pero sin llegar al dolor.' },
  { t: '💚 Deja que la relajación dure más que la tensión', v: 'Deja que la relajación dure más que la tensión.' },
  { t: '💚 Nota el contraste entre tensión y calma', v: 'Nota el contraste entre la tensión y la calma.' },
  { t: '⚠️ En cuello y espalda, tensa muy suave', v: 'En cuello y espalda, tensa muy suave.' },
];

/* Pie de página por defecto (pantalla de inicio) — texto EXACTO del prototipo
   + la sentencia legal añadida por encargo. */
const HOME_FOOTER = '<span class="brand">INSTITUTO MEFIR</span><br>Ante dolor o dudas, consulta a tu fisioterapeuta. ' + MED_DISCLAIMER;
/* A cada pie de rutina se le añade la sentencia legal manteniendo intacto el texto original. */
const withDisclaimer = (footer) => footer + '<br><span style="opacity:.75;font-size:.9em">' + MED_DISCLAIMER + '</span>';

const INITIAL_VIEW = {
  routineNameHtml: '',
  bigText: 'Lista', bigCls: 'g', bigSub: 'Pulsa comenzar',
  cueText: '🫁 Respira con calma para empezar', cueActive: false, cueWarn: false,
  countNum: '', countWord: '',
  ballCls: '', ballScale: 1, ballDur: 0.4,
  pelvicState: 'neutral', pelvicFast: false,
  chips: [], chipsKind: null,
  progressKind: null, repNow: '0', partNow: '–', cycleNow: 0, cycleTot: 0, unit: '',
  doneHtml: '', doneShow: false,
  figReg: null, figState: null,
  boxDot: { top: '6%', left: '6%' }, boxCls: null,
  startLabel: 'Comenzar', resetDisabled: true,
  footerHtml: HOME_FOOTER,
};

/* =========================================================================
   MOTOR (imperativo, fiel al prototipo). Vive en un closure creado UNA vez.
   - Contadores mutables en `st`.
   - Elementos animados con reflow (aro SVG) vía ringRef (imperativo, idéntico).
   - Bola / suelo pélvico / textos / chips / controles vía setView (estado React).
========================================================================= */
function createEngine(ctx) {
  const { setView, setRoutine, setVoiceMsg, ringRef, optsRef } = ctx;
  const upd = (p) => setView((v) => ({ ...v, ...p }));

  const st = {
    routine: null, running: false, timerId: null,
    phase: 'idle', secLeft: 0, phaseTotal: 0,
    part: 0, rep: 0, quickRep: 0, quickIsContract: true,
    cycle: 0, totalCycles: 4,
    tipIndex: 0, bDef: null, seqIdx: 0, bTip: 0,
    jIdx: 0, jShort: true, jGroups: [], jTip: 0,
    startLabel: 'Comenzar',
    infoRoutine: null,
  };

  /* ---- Voz / sonido / vibración (compartido) ---- */
  let voiceES = null;
  let voiceUnlocked = false;
  let keepAliveId = null;
  let audioCtx = null;
  let wakeLock = null;

  function pickVoice() {
    if (!('speechSynthesis' in window)) return;
    const vs = speechSynthesis.getVoices();
    const es = vs.filter((v) => v.lang && v.lang.toLowerCase().startsWith('es'));
    voiceES = es.find((v) => v.localService && /es[-_]ES/i.test(v.lang))
      || es.find((v) => v.localService)
      || es.find((v) => /es[-_]ES/i.test(v.lang)) || es[0] || null;
  }
  function initVoices() {
    if ('speechSynthesis' in window) {
      pickVoice();
      speechSynthesis.onvoiceschanged = pickVoice;
    }
    keepAliveId = setInterval(() => {
      if ('speechSynthesis' in window && speechSynthesis.speaking && !speechSynthesis.paused) {
        try { speechSynthesis.resume(); } catch (e) { /* noop */ }
      }
    }, 5000);
  }
  function unlockVoice() {
    if (voiceUnlocked || !('speechSynthesis' in window)) return;
    try {
      speechSynthesis.resume(); pickVoice();
      const u = new SpeechSynthesisUtterance('​'); u.volume = 0.01;
      if (voiceES) { u.voice = voiceES; u.lang = voiceES.lang; }
      speechSynthesis.speak(u); voiceUnlocked = true;
    } catch (e) { /* noop */ }
  }
  function speak(text, rate = 1.0) {
    if (!optsRef.current.voiceOn || !('speechSynthesis' in window)) return;
    const u = new SpeechSynthesisUtterance(text); u.lang = voiceES ? voiceES.lang : 'es-ES';
    if (voiceES) u.voice = voiceES; u.rate = rate; u.pitch = 1.05; speechSynthesis.speak(u);
  }
  function speakNow(text, rate = 1.0) {
    if (!optsRef.current.voiceOn || !('speechSynthesis' in window)) return;
    speechSynthesis.cancel(); speak(text, rate);
  }
  function beep(freq, dur = 0.12, vol = 0.15) {
    if (!optsRef.current.soundOn) return;
    try {
      audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
      const o = audioCtx.createOscillator(); const g = audioCtx.createGain();
      o.frequency.value = freq; o.type = 'sine';
      g.gain.setValueAtTime(vol, audioCtx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + dur);
      o.connect(g); g.connect(audioCtx.destination); o.start(); o.stop(audioCtx.currentTime + dur);
    } catch (e) { /* noop */ }
  }
  function vibrate(p) { if (optsRef.current.vibrOn && navigator.vibrate) navigator.vibrate(p); }

  /* ---- Helpers visuales ---- */
  function setBig(text, cls, sub = '') { upd({ bigText: text, bigCls: cls, bigSub: sub }); }
  function setCue(text, warn = false) { upd({ cueText: text, cueActive: true, cueWarn: warn }); }
  function setBall(cls, scale, durSec) { upd({ ballCls: cls || '', ballScale: scale, ballDur: durSec }); }
  function resetRing(color) {
    const ring = ringRef.current; if (!ring) return;
    ring.style.transition = 'none'; ring.style.strokeDashoffset = CIRC;
    void ring.getBoundingClientRect(); ring.style.transition = '';
    ring.style.stroke = color;
  }
  function paintRing() {
    const ring = ringRef.current; if (!ring) return;
    const f = st.phaseTotal ? Math.min(1, (st.phaseTotal - st.secLeft + 1) / st.phaseTotal) : 0;
    ring.style.strokeDashoffset = CIRC * (1 - f);
  }
  function ringStroke(color) { if (ringRef.current) ringRef.current.style.stroke = color; }
  function ringSnap(on) { if (ringRef.current) ringRef.current.classList.toggle('snap', !!on); }
  function ringOffset0() { if (ringRef.current) ringRef.current.style.strokeDashoffset = 0; }
  function setPelvic(state, fast = false) { upd({ pelvicState: state, pelvicFast: !!fast }); }
  function setStart(label) { st.startLabel = label; upd({ startLabel: label }); }
  function setReset(disabled) { upd({ resetDisabled: disabled }); }
  function showDone(html) { upd({ doneHtml: html, doneShow: true }); }
  function hideDone() { upd({ doneShow: false }); }

  function ringColor(cls) {
    return cls === 'r' ? 'var(--relax)' : cls === 'h' ? 'var(--hold)' : cls === 'c' ? 'var(--contract)' : 'var(--mefir2)';
  }

  /* =================== MOTOR KEGEL =================== */
  function kSetPhase(np) {
    st.phase = np; const P = PARTS[st.part];
    ringSnap(false); upd({ countWord: '' });
    resetRing('rgba(255,255,255,.7)');
    if (np === 'prep') {
      st.phaseTotal = 3; setBig('Prepárate', '', ''); setCue('🧘‍♀️ Colócate cómoda, pelvis neutra, y respira');
      setBall(null, 1, 0.4); setPelvic('neutral');
      speak('Bienvenida a tu sesión de suelo pélvico del Instituto MEFIR, con la técnica del doctor Kegel. La calidad es mejor que la cantidad: concéntrate. Respira normal y relaja todo menos el suelo pélvico. Uno');
    } else if (np === 'contract') {
      st.phaseTotal = P.contract; setBig('¡CONTRAE!', 'c', 'cierra y eleva hacia dentro · exhala');
      setCue('💨 Exhala suave mientras aprietas. Sin apnea.');
      setBall('c', 0.72, P.contract); ringStroke('var(--contract)'); setPelvic('contract');
      beep(660, 0.15); vibrate(120); speakNow('Contrae');
    } else if (np === 'relax') {
      st.phaseTotal = P.relax; setBig('Relaja', 'r', 'suelta del todo e inhala tranquila');
      if (st.rep % 2 === 0) { const tip = TIPS[st.tipIndex++ % TIPS.length]; setCue(tip.t, true); speakNow('Relaja. ' + tip.v); }
      else { setCue('🌬️ Inhala por la nariz, deja que todo vuelva a su sitio'); speakNow('Relaja del todo'); }
      setBall('r', 1.15, P.relax); ringStroke('var(--relax)'); setPelvic('relax');
      beep(520, 0.15); vibrate(60);
    } else if (np === 'quick') {
      st.phaseTotal = P.quick * 2; st.quickRep = 0; st.quickIsContract = true;
      setBig('¡RÁPIDAS!', '', 'contrae y relaja cada segundo'); setCue('⚡ Al ritmo, sin aguantar el aire');
      ringSnap(true); ringStroke('rgba(255,255,255,.7)');
      speakNow('Ahora rápidas: contrae y relaja cada segundo. Sigue respirando. Y...'); beep(880, 0.1); vibrate(80);
    } else if (np === 'restPart') {
      st.phaseTotal = REST_BETWEEN; setBig('Descanso', '', `la parte ${st.part + 2} empieza sola`);
      setCue('☁️ Respira normal · Instituto MEFIR'); setBall(null, 1, 1); ringStroke('rgba(255,255,255,.7)'); setPelvic('neutral');
      beep(392, 0.2); vibrate([80, 60, 80]); speakNow(`Parte ${NUM_WORDS[st.part + 1]} completada. Descansa y respira. Relaja glúteos, abdomen y muslos.`);
    }
    st.secLeft = st.phaseTotal; kUpdateChips(); kPaint();
    if (np === 'quick') kQuickStep();
  }
  function kQuickStep() {
    if (st.quickIsContract) {
      st.quickRep++; setBig('¡CONTRAE!', 'c', `rápida ${st.quickRep} de ${PARTS[st.part].quick}`);
      setBall('c', 0.72, 0.35); setPelvic('contract', true); beep(700, 0.08); vibrate(60); speakNow(NUM_WORDS[st.quickRep] || String(st.quickRep), 1.25);
    } else {
      setBig('relaja', 'r', 'suelta…'); setBall('r', 1.1, 0.35); setPelvic('relax', true); beep(500, 0.08); speakNow('y relaja', 1.3);
    }
    st.quickIsContract = !st.quickIsContract;
  }
  function kUpdateChips() {
    const chips = [0, 1, 2].map((i) => ({
      lab: ['Parte 1', 'Parte 2', 'Parte 3'][i],
      sub: ['lentas 3s', 'lentas 6s', 'rápidas 1s'][i],
      active: st.phase !== 'idle' && i === st.part,
      done: st.phase !== 'idle' && i < st.part,
    }));
    const partNow = st.phase === 'idle' ? '–' : (st.part + 1) + '/3';
    upd({ chips, chipsKind: 'kegel', partNow });
  }
  function kPaint() {
    const P = PARTS[st.part];
    let countNum; let countWord;
    if (st.phase === 'prep') { const n = Math.min(3, st.phaseTotal - st.secLeft + 1); countNum = n; countWord = NUM_WORDS[n] || ''; }
    else if (st.phase === 'quick') { countNum = st.quickRep; countWord = 'rápidas'; }
    else { countNum = st.secLeft > 0 ? st.secLeft : ''; countWord = st.phase === 'contract' ? 'segundos' : (st.phase === 'relax' ? 'respira' : ''); }
    paintRing();
    const repNow = P.type === 'quick' ? st.quickRep + '/' + P.quick : st.rep + '/' + (P.reps || 0);
    upd({ countNum, countWord, repNow });
  }
  function kBeginPart(i) {
    st.part = i; const P = PARTS[st.part]; st.rep = 0; st.quickRep = 0; speak(P.intro);
    if (P.type === 'slow') { st.rep = 1; kSetPhase('contract'); } else kSetPhase('quick');
  }
  function kEndPart() { if (st.part < PARTS.length - 1) kSetPhase('restPart'); else kFinish(); }
  function kTick() {
    st.secLeft--;
    if (st.phase === 'prep' && st.secLeft > 0) { const n = st.phaseTotal - st.secLeft + 1; beep(440, 0.08, 0.08); speakNow(NUM_WORDS[n] || '', 1.1); }
    if (st.phase === 'quick' && st.secLeft > 0) kQuickStep();
    if (st.secLeft > 0) { kPaint(); return; }
    kPaint(); const P = PARTS[st.part];
    if (st.phase === 'prep') { speakNow('¡Ya!'); kBeginPart(0); }
    else if (st.phase === 'contract') { kSetPhase('relax'); }
    else if (st.phase === 'relax') { if (st.rep < P.reps) { st.rep++; kSetPhase('contract'); } else kEndPart(); }
    else if (st.phase === 'quick') { kEndPart(); }
    else if (st.phase === 'restPart') { kBeginPart(st.part + 1); }
  }
  function kJumpToPart(i) {
    clearInterval(st.timerId); if ('speechSynthesis' in window) speechSynthesis.resume();
    st.running = true; setStart('Pausar'); setReset(false); hideDone();
    kBeginPart(i); st.timerId = setInterval(tick, 1000);
  }
  function kFinish() {
    clearInterval(st.timerId); st.running = false;
    setBig('¡Hecho! 🎉', 'g', 'sesión completa: 3 partes'); upd({ countNum: '', countWord: '' });
    setCue('✨ Respira tranquila, has terminado'); setBall(null, 1, 1); ringOffset0(); ringStroke('var(--mefir2)'); setPelvic('neutral');
    showDone('✨ ¡Sesión del Método completada! Buen trabajo 💪<br><span style="font-weight:600;font-size:.85rem;color:var(--muted)">Complétalo con los ejercicios del Instituto MEFIR</span>');
    setStart('Comenzar');
    beep(523, 0.15); setTimeout(() => beep(659, 0.15), 180); setTimeout(() => beep(784, 0.3), 360); vibrate([100, 80, 100, 80, 200]);
    speakNow('Sesión del Instituto MEFIR completada. Muy buen trabajo. Y sobre todo, complétalo con los ejercicios del Instituto MEFIR que refuerzan tu suelo pélvico.');
    upd({ chips: [0, 1, 2].map((i) => ({ lab: ['Parte 1', 'Parte 2', 'Parte 3'][i], sub: ['lentas 3s', 'lentas 6s', 'rápidas 1s'][i], active: false, done: true })), partNow: '3/3' });
    st.phase = 'idle'; st.part = 0;
  }

  /* =================== MOTOR RESPIRACIONES =================== */
  function bSetPrep() {
    st.phase = 'prep'; ringSnap(false); upd({ countWord: '' });
    resetRing('rgba(255,255,255,.7)');
    st.phaseTotal = 3; setBig('Prepárate', '', ''); setCue(st.bDef.prepCue); setBall(null, 1, 0.4);
    speak(st.bDef.intro);
    st.secLeft = st.phaseTotal; bUpdateChips(); bPaint();
  }
  function bPhase() {
    const s = st.bDef.seq[st.seqIdx]; st.phase = 'seq';
    ringSnap(false); resetRing('rgba(255,255,255,.7)'); upd({ countWord: '' });
    st.phaseTotal = s.dur; setBig(s.label, s.cls, s.sub);
    if (s.tip && st.bDef.tips.length) { const tp = st.bDef.tips[st.bTip++ % st.bDef.tips.length]; setCue('💚 ' + tp); speakNow(s.voice + '. ' + tp); }
    else { setCue(s.cue); speakNow(s.voice); }
    setBall(s.cls, s.scale, s.dur > 1 ? s.dur : 0.35);
    if (s.dot) upd({ boxDot: s.dot, boxCls: s.cls });
    ringStroke(ringColor(s.cls));
    beep(s.beep, 0.15); vibrate(s.vib || 50);
    st.secLeft = st.phaseTotal; bUpdateChips(); bPaint();
  }
  function bUpdateChips() {
    const chips = st.bDef.rounds.map((o) => ({ lab: o.lab, sub: o.sub, active: st.totalCycles === o.val && st.phase === 'idle' }));
    upd({ chips, chipsKind: 'breath', cycleNow: st.phase === 'idle' ? 0 : st.cycle });
  }
  function bPaint() {
    let countNum; let countWord;
    if (st.phase === 'prep') { const n = Math.min(3, st.phaseTotal - st.secLeft + 1); countNum = n; countWord = NUM_WORDS[n] || ''; }
    else { const s = st.bDef.seq[st.seqIdx]; countNum = st.secLeft > 0 ? st.secLeft : ''; countWord = s ? s.word : ''; }
    paintRing();
    upd({ countNum, countWord, cycleNow: st.phase === 'idle' ? 0 : st.cycle });
  }
  function bTick() {
    st.secLeft--;
    if (st.phase === 'prep' && st.secLeft > 0) { const n = st.phaseTotal - st.secLeft + 1; beep(440, 0.08, 0.08); speakNow(NUM_WORDS[n] || '', 1.1); }
    if (st.secLeft > 0) { bPaint(); return; }
    bPaint();
    if (st.phase === 'prep') { speakNow('¡Ya!'); st.cycle = 1; st.seqIdx = 0; bPhase(); return; }
    st.seqIdx++;
    if (st.seqIdx < st.bDef.seq.length) { bPhase(); }
    else if (st.cycle < st.totalCycles) { st.cycle++; st.seqIdx = 0; bPhase(); }
    else bFinish();
  }
  function bFinish() {
    clearInterval(st.timerId); st.running = false;
    setBig('¡Hecho! 🎉', 'g', 'sesión completa'); upd({ countNum: '', countWord: '' });
    setCue('✨ Nota la calma. Vuelve a tu ritmo natural'); setBall(null, 1, 1); ringOffset0(); ringStroke('var(--mefir2)');
    showDone(st.bDef.doneMsg); setStart('Comenzar');
    beep(523, 0.15); setTimeout(() => beep(659, 0.15), 180); setTimeout(() => beep(784, 0.3), 360); vibrate([100, 80, 100]);
    speakNow(st.bDef.doneVoice);
    st.phase = 'idle'; st.cycle = 0; bUpdateChips();
  }
  function markRounds() {
    if (!st.bDef || st.routine === 'kegel') return;
    const chips = st.bDef.rounds.map((o) => ({ lab: o.lab, sub: o.sub, active: st.totalCycles === o.val }));
    upd({ chips, chipsKind: 'breath', cycleTot: st.totalCycles });
  }

  /* =================== MOTOR JACOBSON =================== */
  function jGruposActivos() {
    return st.jShort ? J_CORTA_IDX.map((i) => J_GROUPS[i]) : J_GROUPS.slice();
  }
  function jSetPhase(np) {
    st.phase = np; ringSnap(false); upd({ countWord: '' });
    resetRing('rgba(255,255,255,.7)');
    if (np === 'prep') {
      st.phaseTotal = J_PREP; setBig('Prepárate', '', '');
      setCue('Ponte cómoda, cierra los ojos si quieres, y respira');
      setBall(null, 1, 0.4); upd({ figReg: null, figState: null });
      speak('Relajación muscular progresiva, del Instituto MEFIR. Ponte cómoda. Iremos tensando y soltando cada grupo de músculos. Respira con normalidad. Uno');
    } else if (np === 'jtense') {
      const g = st.jGroups[st.jIdx]; st.phaseTotal = J_TENSE;
      setBig('TENSA', 'c', g.name); setCue(g.cue + '. Mantén…');
      setBall('c', 0.72, J_TENSE); upd({ figReg: g.reg, figState: 'tense' });
      ringStroke('var(--contract)'); beep(660, 0.15); vibrate(120);
      speakNow('Tensa. ' + g.cue + '. Mantén.');
    } else if (np === 'jrelax') {
      const g = st.jGroups[st.jIdx]; st.phaseTotal = J_RELAX;
      setBig('SUELTA', 'r', 'nota cómo se relaja');
      if (st.jIdx % 2 === 0) {
        const tp = J_TIPS[st.jTip++ % J_TIPS.length];
        setCue(tp.t, tp.t[0] === '⚠');
        speakNow('Y suelta. ' + g.name + ' se relaja, pesado y cálido. ' + tp.v);
      } else {
        setCue('Suelta del todo. Siente ' + g.name.toLowerCase() + ' pesado y en calma');
        speakNow('Y suelta. Deja ' + g.name + ' pesado, cálido y en calma.');
      }
      setBall('r', 1.15, J_RELAX); upd({ figReg: g.reg, figState: 'relax' });
      ringStroke('var(--relax)'); beep(440, 0.15); vibrate(60);
    }
    st.secLeft = st.phaseTotal; jUpdateChips(); jPaint();
  }
  function jUpdateChips() {
    const chips = [
      { lab: 'Corta', sub: '8 grupos · ~2 min', active: st.jShort && st.phase === 'idle' },
      { lab: 'Completa', sub: '14 grupos · ~4 min', active: !st.jShort && st.phase === 'idle' },
    ];
    upd({ chips, chipsKind: 'breath', cycleTot: st.jGroups.length || (st.jShort ? 8 : 14) });
  }
  function jPaint() {
    let countNum; let countWord;
    if (st.phase === 'prep') { const n = Math.min(J_PREP, st.phaseTotal - st.secLeft + 1); countNum = n; countWord = NUM_WORDS[n] || ''; }
    else { countNum = st.secLeft > 0 ? st.secLeft : ''; countWord = st.phase === 'jtense' ? 'tensa' : 'suelta'; }
    paintRing();
    upd({ countNum, countWord, cycleNow: st.phase === 'idle' ? 0 : st.jIdx + 1 });
  }
  function jTick() {
    st.secLeft--;
    if (st.phase === 'prep' && st.secLeft > 0) { const n = J_PREP - st.secLeft + 1; beep(440, 0.08, 0.08); speakNow(NUM_WORDS[n] || '', 1.1); }
    if (st.secLeft > 0) { jPaint(); return; }
    jPaint();
    if (st.phase === 'prep') { speakNow('Empezamos.'); st.jIdx = 0; jSetPhase('jtense'); }
    else if (st.phase === 'jtense') { jSetPhase('jrelax'); }
    else if (st.phase === 'jrelax') {
      if (st.jIdx < st.jGroups.length - 1) { st.jIdx++; jSetPhase('jtense'); } else jFinish();
    }
  }
  function jFinish() {
    clearInterval(st.timerId); st.running = false;
    setBig('Hecho', 'g', 'cuerpo relajado'); upd({ countNum: '', countWord: '' });
    setCue('Quédate unos segundos disfrutando de la calma');
    setBall(null, 1, 1); upd({ figReg: null, figState: null });
    ringOffset0(); ringStroke('var(--mefir2)');
    showDone('Sesión completada. Buen trabajo.<br><span style="font-weight:600;font-size:.85rem;color:var(--muted)">Practícala a diario · Instituto MEFIR</span>');
    setStart('Comenzar');
    beep(523, 0.15); setTimeout(() => beep(659, 0.15), 180); setTimeout(() => beep(784, 0.3), 360); vibrate([100, 80, 100]);
    speakNow('Sesión completada. Todo tu cuerpo está más relajado. Quédate unos momentos disfrutando de esta calma.');
    st.phase = 'idle'; jUpdateChips();
  }

  /* =================== DISPATCHER + CONTROLES =================== */
  function tick() { if (st.routine === 'kegel') kTick(); else if (st.routine === 'jacobson') jTick(); else bTick(); }

  function start() {
    st.running = true;
    if (st.routine === 'kegel') { st.part = 0; st.rep = 0; st.quickRep = 0; }
    else if (st.routine === 'jacobson') { st.jIdx = 0; st.jGroups = jGruposActivos(); }
    else { st.cycle = 0; }
    hideDone(); setStart('Pausar'); setReset(false);
    if ('speechSynthesis' in window) speechSynthesis.resume();
    if (st.routine === 'kegel') kSetPhase('prep'); else if (st.routine === 'jacobson') jSetPhase('prep'); else bSetPrep();
    st.timerId = setInterval(tick, 1000);
    requestWake();
  }
  function pause() {
    st.running = false; clearInterval(st.timerId);
    if ('speechSynthesis' in window) speechSynthesis.cancel();
    setStart('Continuar'); setCue('⏸️ En pausa');
  }
  function resume() { st.running = true; setStart('Pausar'); st.timerId = setInterval(tick, 1000); requestWake(); }

  function hardReset() {
    clearInterval(st.timerId); st.running = false; if ('speechSynthesis' in window) speechSynthesis.cancel();
    st.phase = 'idle'; st.part = 0; st.rep = 0; st.quickRep = 0; st.cycle = 0; st.seqIdx = 0; st.secLeft = 0; st.phaseTotal = 0;
    setStart('Comenzar'); setReset(true);
    setBig('Lista', 'g', (st.routine && st.routine !== 'kegel') ? 'Elige y pulsa comenzar' : 'Pulsa comenzar · la sesión avanza sola');
    upd({
      countNum: '', countWord: '',
      cueText: '🫁 Respira con calma para empezar', cueActive: false, cueWarn: false,
      ballCls: '', ballScale: 1, ballDur: 0.4,
      doneShow: false, pelvicState: 'neutral', pelvicFast: false,
      figReg: null, figState: null,
      boxDot: { top: '6%', left: '6%' }, boxCls: null,
    });
    ringSnap(false); resetRing('rgba(255,255,255,.7)');
    if (st.routine === 'kegel') { kUpdateChips(); upd({ repNow: '0' }); }
    else if (st.routine === 'jacobson') { st.jGroups = jGruposActivos(); jUpdateChips(); upd({ cycleNow: 0 }); }
    else if (st.routine) { bUpdateChips(); markRounds(); }
  }

  function onStartClick() {
    if (st.phase === 'idle' && !st.running) { if (st.startLabel === 'Comenzar') start(); else resume(); }
    else if (st.running) pause(); else resume();
  }

  /* ---- Navegación inicio <-> rutina ---- */
  function goHome() {
    hardReset();
    st.routine = null; setRoutine(null);
    upd({ routineNameHtml: '', footerHtml: HOME_FOOTER });
  }
  function enterRoutine(name) {
    st.routine = name; setRoutine(name);
    if (name === 'kegel') {
      upd({
        routineNameHtml: 'Kegel · Suelo pélvico<br><span style="font-size:.72rem;color:var(--muted);font-weight:600">método del Dr. Arnold Kegel</span>',
        progressKind: 'kegel',
        footerHtml: withDisclaimer('Contrae cerrando y elevando hacia dentro (nunca empujes hacia abajo), a intensidad media, con glúteos, abdomen y muslos relajados y sin aguantar la respiración. Y sobre todo, complétalo con los ejercicios del <b style="color:var(--mefir2)">Instituto MEFIR</b>. No hagas kegels mientras orinas. Técnica desarrollada por el <b>Dr. Arnold Kegel</b>, ginecólogo.<br><br><span class="brand">INSTITUTO MEFIR</span>'),
      });
    } else if (name === 'jacobson') {
      st.jShort = true; st.jGroups = jGruposActivos();
      upd({
        routineNameHtml: 'Relajación muscular progresiva<br><span style="font-size:.72rem;color:var(--muted);font-weight:600">método del Dr. Edmund Jacobson</span>',
        progressKind: 'breath', unit: 'grupo', cycleTot: st.jGroups.length,
        footerHtml: withDisclaimer('Tensa 5 segundos con firmeza pero sin llegar al dolor, sin aguantar la respiración, y suelta 12 segundos: la relajación dura más que la tensión, a propósito. Técnica desarrollada por el <b>Dr. Edmund Jacobson</b>, médico.<br><br><span class="brand">INSTITUTO MEFIR</span>'),
      });
    } else {
      const d = BREATHS[name]; st.bDef = d;
      st.totalCycles = d.defaultRounds || d.rounds[0].val;
      upd({
        routineNameHtml: d.name + '<br><span style="font-size:.72rem;color:var(--muted);font-weight:600">' + d.sub + '</span>',
        progressKind: 'breath', unit: d.unit, cycleTot: st.totalCycles,
        footerHtml: withDisclaimer(d.footer),
      });
      markRounds();
    }
    hardReset();
  }

  /* ---- Chips ---- */
  function chipClick(i) {
    if (st.routine === 'kegel') { kJumpToPart(i); return; }
    if (st.routine === 'jacobson') {
      if (st.phase === 'idle') { st.jShort = i === 0; st.jGroups = jGruposActivos(); jUpdateChips(); }
      return;
    }
    if (st.phase === 'idle' && st.bDef) { st.totalCycles = st.bDef.rounds[i].val; markRounds(); }
  }

  /* ---- Fichas "?" ---- */
  function openInfo(key) { st.infoRoutine = key; }
  function goFromInfo(key) { enterRoutine(key); }

  /* ---- Probar voz ---- */
  function testVoice() {
    if (!('speechSynthesis' in window)) { setVoiceMsg('⚠️ Este navegador no admite voz. Ábrelo en el móvil (Chrome/Safari).'); return; }
    if (!optsRef.current.voiceOn) { setVoiceMsg('Activa la casilla 🎙️ Voz primero.'); return; }
    if (st.running) { setVoiceMsg('Pausa la rutina antes de probar.'); return; }
    unlockVoice(); const all = speechSynthesis.getVoices();
    if (all.length === 0) { setVoiceMsg('⚠️ Este dispositivo no tiene voces instaladas (0). Ábrelo en el móvil, donde siempre hay voz.'); return; }
    pickVoice(); const chosen = voiceES || all[0]; speechSynthesis.resume();
    const u = new SpeechSynthesisUtterance('Hola, soy tu guía del Instituto MEFIR. Si me oyes, la voz funciona.');
    u.lang = chosen.lang; u.voice = chosen; u.rate = 1; u.pitch = 1.05; u.volume = 1;
    u.onstart = () => setVoiceMsg('🔊 Sonando con: ' + chosen.name + ' (' + chosen.lang + ')');
    u.onerror = (e) => setVoiceMsg((e.error === 'canceled' || e.error === 'interrupted') ? 'Reintenta pulsando otra vez 🔊.' : '⚠️ Error: ' + (e.error || '?') + ' · voces: ' + all.length);
    u.onend = () => setVoiceMsg('✓ Voz OK: ' + chosen.name);
    speechSynthesis.speak(u);
    setVoiceMsg('Detectadas ' + all.length + ' voces. Probando…');
  }

  /* ---- Pantalla encendida ---- */
  async function requestWake() {
    try { if ('wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen'); } catch (e) { /* noop */ }
  }
  function isRunning() { return st.running; }

  function destroy() {
    clearInterval(st.timerId);
    if (keepAliveId) clearInterval(keepAliveId);
    if ('speechSynthesis' in window) { try { speechSynthesis.cancel(); speechSynthesis.onvoiceschanged = null; } catch (e) { /* noop */ } }
    if (wakeLock) { try { wakeLock.release(); } catch (e) { /* noop */ } wakeLock = null; }
  }

  return {
    initVoices, unlockVoice, requestWake, isRunning, destroy,
    onStartClick, hardReset, goHome, enterRoutine, chipClick, openInfo, goFromInfo, testVoice,
  };
}

/* =========================================================================
   COMPONENTE
========================================================================= */
// Icono por rutina (para el home agrupado que viene de la config del área).
const ICO_POR_KEY = { kegel: ICO.pelvis, breath: ICO.lungs, sigh: ICO.lungs, coherence: ICO.heart, box: ICO.boxb, jacobson: ICO.body };

export default function RutinasMefir({ config = null }) {
  // `config` (opcional) = estructura de /canal-apps/cliente: { titulo_canal, grupos:
  // [{id,label,apps:[{key,titulo,subtitulo}]}] }. Si viene, el home se pinta AGRUPADO
  // por sub-enunciado (Ann 2026-07-25). Si no, se mantiene el home fijo de siempre.
  const [view, setView] = useState(INITIAL_VIEW);
  const [routine, setRoutine] = useState(null); // null = pantalla inicio
  const [infoKey, setInfoKey] = useState(null);
  const [voiceMsg, setVoiceMsg] = useState('');
  const [voiceOn, setVoiceOn] = useState(true);
  const [soundOn, setSoundOn] = useState(true);
  const [vibrOn, setVibrOn] = useState(true);

  const ringRef = useRef(null);
  const optsRef = useRef({ voiceOn, soundOn, vibrOn });
  useEffect(() => { optsRef.current = { voiceOn, soundOn, vibrOn }; }, [voiceOn, soundOn, vibrOn]);

  const engineRef = useRef(null);
  if (!engineRef.current) {
    engineRef.current = createEngine({ setView, setRoutine, setVoiceMsg, ringRef, optsRef });
  }
  const eng = engineRef.current;

  useEffect(() => {
    eng.initVoices();
    const onDown = () => eng.unlockVoice();
    document.addEventListener('pointerdown', onDown, { once: true });
    document.addEventListener('click', onDown, { once: true });
    const onVis = () => { if (eng.isRunning() && document.visibilityState === 'visible') eng.requestWake(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      eng.destroy();
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('click', onDown);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [eng]);

  const openInfo = (key) => { eng.openInfo(key); setInfoKey(key); };
  const closeInfo = () => setInfoKey(null);
  const goFromInfo = () => { const k = infoKey; setInfoKey(null); if (k) eng.goFromInfo(k); };

  const cueCls = 'breath-cue' + (view.cueActive ? ' active' : '') + (view.cueWarn ? ' warn' : '');
  const ballCls = 'breath' + (view.ballCls ? ' ' + view.ballCls + '-phase' : '');
  const pelvicCls = 'pelvic' + (view.pelvicFast ? ' pf-fast' : '')
    + (view.pelvicState === 'contract' ? ' pf-contract' : view.pelvicState === 'relax' ? ' pf-relax' : '');

  return (
    <div className="rmef">
      <style>{RMEF_CSS}</style>

      <header>
        <div className="routine" dangerouslySetInnerHTML={{ __html: view.routineNameHtml }} />
      </header>

      {/* ===== INICIO: selector de programa ===== */}
      {/* Home AGRUPADO por sub-enunciado (cuando el área trae config administrada). */}
      {routine === null && config && Array.isArray(config.grupos) && config.grupos.some(g => (g.apps || []).length) && (
        <div id="home">
          {config.grupos.filter(g => (g.apps || []).length).map(g => (
            <div className="rmef-grupo" key={g.id || g.label}>
              {g.label ? <div className="rmef-subenun">{g.label}</div> : null}
              {(g.apps || []).map(app => (
                <div className="rrow" key={app.key}>
                  <div className="rcard" onClick={() => eng.enterRoutine(app.key)}>
                    <span className="ic">{ICO_POR_KEY[app.key] || ICO.lungs}</span>
                    <span><b>{app.titulo || app.key}</b>{app.subtitulo ? <small>{app.subtitulo}</small> : null}</span>
                  </div>
                  <button className="info-btn" onClick={(e) => { e.stopPropagation(); openInfo(app.key); }} aria-label="Más información">?</button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {/* Home FIJO de siempre (sin config o config vacía) — fallback, no rompe nada. */}
      {routine === null && !(config && Array.isArray(config.grupos) && config.grupos.some(g => (g.apps || []).length)) && (
        <div id="home">
          <div className="lead">Respiraciones terapéuticas</div>
          <div className="rrow">
            <div className="rcard" onClick={() => eng.enterRoutine('kegel')}>
              <span className="ic">{ICO.pelvis}</span>
              <span><b>Kegel · Suelo pélvico</b><small>Fortalece el suelo pélvico · método del Dr. Arnold Kegel</small></span>
            </div>
            <button className="info-btn" onClick={(e) => { e.stopPropagation(); openInfo('kegel'); }} aria-label="Más información">?</button>
          </div>
          <div className="rrow">
            <div className="rcard" onClick={() => eng.enterRoutine('breath')}>
              <span className="ic">{ICO.lungs}</span>
              <span><b>Respiración 4-7-8</b><small>Relaja y ayuda a dormir · Dr. Andrew Weil</small></span>
            </div>
            <button className="info-btn" onClick={(e) => { e.stopPropagation(); openInfo('breath'); }} aria-label="Más información">?</button>
          </div>
          <div className="rrow">
            <div className="rcard" onClick={() => eng.enterRoutine('sigh')}>
              <span className="ic">{ICO.lungs}</span>
              <span><b>Suspiro fisiológico</b><small>Calma el estrés en 1-2 min · Dr. Andrew Huberman</small></span>
            </div>
            <button className="info-btn" onClick={(e) => { e.stopPropagation(); openInfo('sigh'); }} aria-label="Más información">?</button>
          </div>
          <div className="rrow">
            <div className="rcard" onClick={() => eng.enterRoutine('box')}>
              <span className="ic">{ICO.boxb}</span>
              <span><b>Respiración cuadrada</b><small>Calma el estrés y mejora la concentración · 4-4-4-4</small></span>
            </div>
            <button className="info-btn" onClick={(e) => { e.stopPropagation(); openInfo('box'); }} aria-label="Más información">?</button>
          </div>
          <div className="rrow">
            <div className="rcard" onClick={() => eng.enterRoutine('coherence')}>
              <span className="ic">{ICO.heart}</span>
              <span><b>Coherencia cardíaca</b><small>Equilibra y baja la tensión · Dr. David O’Hare</small></span>
            </div>
            <button className="info-btn" onClick={(e) => { e.stopPropagation(); openInfo('coherence'); }} aria-label="Más información">?</button>
          </div>

          {/* SECCIÓN NUEVA (Ann 2026-07-26) — SOLO en la versión de suscripción */}
          <div className="lead lead-seccion">Relajación y manejo del estrés</div>
          <div className="rrow">
            <div className="rcard" onClick={() => eng.enterRoutine('jacobson')}>
              <span className="ic">{ICO.body}</span>
              <span><b>Relajación muscular progresiva</b><small>Tensa y suelta grupo a grupo · Dr. Edmund Jacobson</small></span>
            </div>
            <button className="info-btn" onClick={(e) => { e.stopPropagation(); openInfo('jacobson'); }} aria-label="Más información">?</button>
          </div>
        </div>
      )}

      {/* ===== Ficha informativa (botón "?") — pantalla de solo texto sobre fondo ===== */}
      {infoKey && (
        <div className="modal-ov open" onClick={(e) => { if (e.currentTarget === e.target) closeInfo(); }}>
          <div className="modal-back-wrap"><button className="modal-back" onClick={closeInfo}>← Volver</button></div>
          <div className="modal-title" dangerouslySetInnerHTML={{ __html: INFO[infoKey].title }} />
          <div className="modal-body" dangerouslySetInnerHTML={{ __html: INFO[infoKey].body }} />
          <button className="btn btn-primary" onClick={goFromInfo}>Empezar esta rutina</button>
        </div>
      )}

      {/* ===== RUTINA ===== */}
      {routine !== null && (
        <div id="app">
          <div className="backbar">
            <button className="back-btn" onClick={() => eng.goHome()}>← Volver al menú</button>
            {/* «? Qué es» en TODAS las rutinas (Ann 2026-07-26): abre su ficha explicativa */}
            <button className="back-btn" onClick={() => openInfo(routine)}>? Qué es</button>
          </div>

          <div className="chips">
            {view.chips.map((c, i) => (
              <div
                key={i}
                className={'chip' + (c.active ? ' active' : '') + (c.done ? ' done' : '')}
                onClick={() => eng.chipClick(i)}
              >
                <b>{c.lab}</b>{c.sub}
              </div>
            ))}
          </div>

          <div className={'big-phase ' + view.bigCls}>{view.bigText}</div>
          <div className="big-sub">{view.bigSub}</div>

          <div className="stage">
            <div className="circle-wrap">
              {routine === 'box' && (
                <div className="boxguide" aria-hidden="true">
                  <span className={`boxdot ${view.boxCls || ''}`} style={{ top: view.boxDot.top, left: view.boxDot.left }} />
                </div>
              )}
              <svg className="ring" viewBox="0 0 100 100">
                <circle className="ring-bg" cx="50" cy="50" r="46" />
                <circle
                  className="ring-fg" ref={ringRef} cx="50" cy="50" r="46"
                  strokeDasharray="289.03" strokeDashoffset="289.03"
                />
              </svg>
              <div className={ballCls} style={{ transitionDuration: view.ballDur + 's', transform: 'scale(' + view.ballScale + ')' }}>
                <div className="count">{view.countNum}</div>
                <div className="count-word">{view.countWord}</div>
              </div>
            </div>

            {routine === 'jacobson' && (
              <div className="jfig-wrap">
                <svg className="jfig" viewBox="0 0 120 290" aria-hidden="true">
                  <ellipse className={`reg ${view.figReg === 'head' ? view.figState : ''}`} cx="60" cy="26" rx="16" ry="18" />
                  <rect className={`reg ${view.figReg === 'neck' ? view.figState : ''}`} x="53" y="42" width="14" height="10" rx="4" />
                  <rect className={`reg ${view.figReg === 'shoulders' ? view.figState : ''}`} x="37" y="54" width="46" height="14" rx="7" />
                  <rect className={`reg ${view.figReg === 'chest' ? view.figState : ''}`} x="42" y="70" width="36" height="28" rx="8" />
                  <rect className={`reg ${view.figReg === 'abdomen' ? view.figState : ''}`} x="44" y="100" width="32" height="26" rx="8" />
                  <rect className={`reg ${view.figReg === 'lowback' ? view.figState : ''}`} x="43" y="128" width="34" height="16" rx="6" />
                  <rect className={`reg ${view.figReg === 'glutes' ? view.figState : ''}`} x="42" y="144" width="36" height="18" rx="9" />
                  <rect className={`reg ${view.figReg === 'arms' ? view.figState : ''}`} x="25" y="58" width="12" height="92" rx="6" />
                  <rect className={`reg ${view.figReg === 'arms' ? view.figState : ''}`} x="83" y="58" width="12" height="92" rx="6" />
                  <ellipse className={`reg ${view.figReg === 'hands' ? view.figState : ''}`} cx="31" cy="158" rx="8" ry="9" />
                  <ellipse className={`reg ${view.figReg === 'hands' ? view.figState : ''}`} cx="89" cy="158" rx="8" ry="9" />
                  <rect className={`reg ${view.figReg === 'thighs' ? view.figState : ''}`} x="45" y="162" width="13" height="52" rx="6" />
                  <rect className={`reg ${view.figReg === 'thighs' ? view.figState : ''}`} x="62" y="162" width="13" height="52" rx="6" />
                  <rect className={`reg ${view.figReg === 'calves' ? view.figState : ''}`} x="46" y="214" width="11" height="48" rx="5" />
                  <rect className={`reg ${view.figReg === 'calves' ? view.figState : ''}`} x="63" y="214" width="11" height="48" rx="5" />
                  <ellipse className={`reg ${view.figReg === 'feet' ? view.figState : ''}`} cx="50" cy="270" rx="9" ry="6" />
                  <ellipse className={`reg ${view.figReg === 'feet' ? view.figState : ''}`} cx="70" cy="270" rx="9" ry="6" />
                </svg>
                <div className="jfig-label">grupo activo</div>
              </div>
            )}

            {routine === 'kegel' && (
              <div className={pelvicCls}>
                <svg viewBox="0 0 120 80">
                  <ellipse className="pf-bone" cx="14" cy="34" rx="7" ry="9" />
                  <ellipse className="pf-bone" cx="106" cy="34" rx="7" ry="9" />
                  <path className="pf-muscle" d="M14,36 Q60,72 106,36 Q60,58 14,36 Z" />
                  <path className="pf-arrow up" d="M60,58 L60,40 M53,47 L60,40 L67,47" />
                  <path className="pf-arrow down" d="M60,44 L60,62 M53,55 L60,62 L67,55" />
                </svg>
                <div className="pf-label">suelo pélvico</div>
              </div>
            )}
          </div>

          <div className={cueCls}><span>{view.cueText}</span></div>

          <div className="progress-row">
            {view.progressKind === 'kegel' && (
              <>
                <div><b>{view.repNow}</b><br />repetición</div>
                <div><b>{view.partNow}</b><br />parte</div>
              </>
            )}
            {view.progressKind === 'breath' && (
              <div><b>{view.cycleNow}</b>/<span>{view.cycleTot}</span><br />{view.unit}</div>
            )}
          </div>

          <div className="controls">
            <button className="btn btn-primary" onClick={() => eng.onStartClick()}>{view.startLabel}</button>
            <button className="btn btn-secondary" disabled={view.resetDisabled} onClick={() => eng.hardReset()}>Reiniciar</button>
          </div>

          {/* Controles de audio/vibración: permiten APAGAR voz/sonido/vibración (Ann) */}
          <div className="options">
            <label><input type="checkbox" checked={voiceOn} onChange={(e) => setVoiceOn(e.target.checked)} /> 🎙️ Voz</label>
            <label><input type="checkbox" checked={soundOn} onChange={(e) => setSoundOn(e.target.checked)} /> 🔔 Sonido</label>
            <label><input type="checkbox" checked={vibrOn} onChange={(e) => setVibrOn(e.target.checked)} /> 📳 Vibración</label>
          </div>
          <button id="testVoice" onClick={() => eng.testVoice()}>🔊 Probar voz</button>
          <div id="voiceMsg">{voiceMsg}</div>

          {view.doneShow && (
            <div className="done-msg" style={{ display: 'block' }} dangerouslySetInnerHTML={{ __html: view.doneHtml }} />
          )}
        </div>
      )}

      {/* Pie de la PORTADA con el logo MEFIR (paridad con /regalo, Ann 2026-07-25):
          antes el pie solo salía dentro de una rutina. Se mantiene la frase legal
          «No sustituye consejo médico.» que ya tenía el canal. */}
      {routine === null && (
        <footer>
          <a href="https://institutomefir.com" target="_blank" rel="noopener noreferrer" title="Instituto MEFIR">
            <img className="pd-mefir-logo" src="/logo-mefir-blanco.png" alt="Instituto MEFIR" />
          </a><br />
          <span className="brand">Instituto de Medicina Física y Rehabilitación</span><br />
          Ante dolor o dudas, consulta a tu fisioterapeuta.
          <br /><span style={{ opacity: .75, fontSize: '.9em' }}>{MED_DISCLAIMER}</span>
        </footer>
      )}
      {routine !== null && <footer dangerouslySetInnerHTML={{ __html: view.footerHtml }} />}
    </div>
  );
}

/* =========================================================================
   CSS — copia del prototipo, con TODOS los selectores acotados a `.rmef`
   para no filtrar estilos al resto del Canal. Colores/tamaños EXACTOS.
========================================================================= */
const RMEF_CSS = `
.rmef{
  --mefir:#1FAD90; --mefir2:#00C99D; --bg1:#071a1a; --bg2:#071a1a;
  --card:rgba(255,255,255,.07);
  --text:#f2faf7; --muted:#9ec4b8;
  --contract:#f05252;  /* rojo = contraer / exhalar fuerte */
  --relax:#4da3ff;     /* azul = relajar / inhalar */
  --hold:#ffce54;      /* ámbar = sostener */
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  background:transparent;
  color:var(--text); min-height:100dvh;
  display:flex; flex-direction:column; align-items:center;
  padding:max(env(safe-area-inset-top),12px) 20px max(env(safe-area-inset-bottom),20px);
  user-select:none; -webkit-tap-highlight-color:transparent;
}
.rmef *{margin:0;padding:0;box-sizing:border-box;-webkit-tap-highlight-color:transparent}
.rmef header{text-align:center; margin-bottom:10px}
.rmef header .logo{width:min(66vw,250px); height:auto; margin-bottom:6px}
.rmef header .routine{font-size:.95rem; font-weight:700; color:var(--text); margin-top:2px; min-height:1.1em}

/* ---------- PANTALLA DE INICIO ---------- */
.rmef #home{width:100%; max-width:420px; display:flex; flex-direction:column; gap:12px; margin-top:6px}
.rmef #home .lead{font-size:1.05rem; font-weight:700; color:#fff; text-align:center; margin-bottom:10px}
.rmef .rmef-grupo{display:flex; flex-direction:column; gap:12px; width:100%}
.rmef .rmef-subenun{font-size:.9rem; font-weight:800; color:var(--mefir2); text-align:left;
  letter-spacing:.02em; margin:6px 0 0}
.rmef .rcard{
  display:flex; align-items:center; gap:14px; text-align:left;
  background:var(--card); border:1.5px solid transparent; border-radius:18px;
  padding:16px; cursor:pointer; transition:all .2s; color:var(--text);
}
.rmef .rcard:active{transform:scale(.98)}
.rmef .rcard:hover{border-color:var(--mefir)}
.rmef .rcard .ic{width:46px; height:46px; flex:none;
  display:flex; align-items:center; justify-content:center}
.rmef .rcard .ic svg{width:34px; height:34px; display:block}
.rmef .rcard b{font-size:1.05rem; display:block}
.rmef .rrow{display:flex; align-items:stretch; gap:8px}
.rmef .rrow .rcard{flex:1}
.rmef .info-btn{flex:none; width:42px; border:none; border-radius:14px; background:var(--card);
  color:var(--mefir2); font-size:1.2rem; font-weight:800; cursor:pointer; transition:all .2s}
.rmef .info-btn:active{transform:scale(.94)} .rmef .info-btn:hover{background:rgba(31,173,144,.18)}
.rmef .modal-ov{display:none; position:fixed; inset:0; background:linear-gradient(160deg,var(--bg1),var(--bg2)); z-index:50;
  flex-direction:column; align-items:center; justify-content:flex-start; padding:72px 28px 40px; text-align:center; overflow-y:auto}
.rmef .modal-ov.open{display:flex; animation:rmefpop .3s ease}
.rmef .modal-back-wrap{width:100%; max-width:340px; text-align:left; margin-bottom:20px}
.rmef .modal-back{display:inline-flex; align-items:center; gap:6px;
  color:var(--mefir2); background:rgba(7,26,26,.55); border:1px solid rgba(31,173,144,.45);
  border-radius:999px; padding:8px 16px; font-size:.85rem; font-weight:700; cursor:pointer}
.rmef .modal-title{font-size:1.3rem; font-weight:800; color:var(--mefir2); margin-bottom:16px; max-width:340px}
.rmef .modal-body{font-size:1rem; color:var(--text); line-height:1.6; margin-bottom:26px; max-width:340px}
.rmef .modal-body small{color:var(--muted); font-size:.82rem}
.rmef .modal-ov .btn{flex:0 0 auto; align-self:center; width:auto; min-width:220px; padding:14px 28px}
.rmef .rcard small{color:var(--muted); font-size:.8rem}

/* ---------- PANTALLA DE RUTINA ---------- */
.rmef #app{width:100%; max-width:420px; display:flex; flex-direction:column; align-items:center}
.rmef .backbar{width:100%; display:flex; align-items:center; margin-bottom:8px}
.rmef .back-btn{display:inline-flex; align-items:center; gap:6px; color:var(--mefir2);
  background:rgba(7,26,26,.55); border:1px solid rgba(31,173,144,.45); border-radius:999px;
  padding:8px 16px; font-size:.85rem; font-weight:700; cursor:pointer; min-height:44px}
.rmef .back-btn:active{transform:scale(.96)}

.rmef .big-phase{
  font-size:clamp(2.1rem, 10vw, 3rem); font-weight:900; letter-spacing:1px;
  text-align:center; min-height:1.2em; line-height:1.1; margin-bottom:2px; color:#fff;
  transition:color .3s;
}
.rmef .big-phase.c{color:var(--contract)} .rmef .big-phase.r{color:var(--relax)}
.rmef .big-phase.h{color:var(--hold)} .rmef .big-phase.g{color:var(--mefir2)}
.rmef .big-sub{font-size:.9rem; color:var(--muted); text-align:center; min-height:1.2em; margin-bottom:8px; font-weight:600}

.rmef .chips{display:flex; gap:8px; margin-bottom:10px; width:100%}
.rmef .chip{
  flex:1; padding:8px 4px; border-radius:14px; text-align:center;
  background:var(--card); color:var(--muted); font-size:.75rem; font-weight:600;
  border:1.5px solid transparent; transition:all .3s; cursor:pointer;
}
.rmef .chip b{display:block; font-size:.85rem; color:inherit}
.rmef .chip.active{background:rgba(31,173,144,.22); color:var(--text); border-color:var(--mefir)}
.rmef .chip.done{opacity:.55}
.rmef .chip.done b::after{content:" ✓"; color:var(--mefir2)}
.rmef .chip:active{transform:scale(.96)}

.rmef .stage{display:flex; align-items:center; justify-content:center; gap:10px; margin:4px 0 10px; flex-wrap:nowrap}
.rmef .circle-wrap{position:relative; width:min(64vw,260px); height:min(64vw,260px);
  display:flex; align-items:center; justify-content:center}
.rmef svg.ring{position:absolute; inset:0; transform:rotate(-90deg)}
.rmef .ring-bg{fill:none; stroke:rgba(255,255,255,.1); stroke-width:6}
.rmef .ring-fg{fill:none; stroke:#fff; stroke-width:6; stroke-linecap:round;
  transition:stroke-dashoffset .95s linear, stroke .4s}
.rmef .ring-fg.snap{transition:stroke-dashoffset .15s ease, stroke .2s}
.rmef .breath{
  width:58%; height:58%; border-radius:50%;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  transition:transform .3s ease, background .5s, box-shadow .5s, border-color .5s;
  transform:scale(1);
  background:transparent; border:2px solid rgba(255,255,255,.25); box-shadow:none;
}
.rmef .breath.c-phase{border-color:transparent;
  background:radial-gradient(circle at 35% 30%, rgba(255,255,255,.3), rgba(255,255,255,0) 55%),linear-gradient(140deg,#f05252,#e02424);
  box-shadow:0 0 60px rgba(240,82,82,.5)}
.rmef .breath.r-phase{border-color:transparent;
  background:radial-gradient(circle at 35% 30%, rgba(255,255,255,.3), rgba(255,255,255,0) 55%),linear-gradient(140deg,#4da3ff,#2f7de0);
  box-shadow:0 0 60px rgba(77,163,255,.5)}
.rmef .breath.h-phase{border-color:transparent;
  background:radial-gradient(circle at 35% 30%, rgba(255,255,255,.3), rgba(255,255,255,0) 55%),linear-gradient(140deg,#ffce54,#f0a500);
  box-shadow:0 0 60px rgba(255,206,84,.5)}
.rmef .breath.g-phase{border-color:transparent;
  background:radial-gradient(circle at 35% 30%, rgba(255,255,255,.3), rgba(255,255,255,0) 55%),linear-gradient(140deg,#1FAD90,#00C99D);
  box-shadow:0 0 60px rgba(31,173,144,.5)}
.rmef .count{font-size:3.6rem; font-weight:800; line-height:1; font-variant-numeric:tabular-nums;
  color:#fff; text-shadow:0 2px 12px rgba(0,0,0,.4)}
.rmef .count-word{font-size:.85rem; font-weight:700; opacity:.8; margin-top:4px; text-transform:uppercase; letter-spacing:1px; color:#fff}

.rmef .pelvic{display:flex; flex-direction:column; align-items:center; gap:4px; width:min(26vw,110px)}
.rmef .pelvic svg{width:100%; height:auto; overflow:visible}
.rmef .pf-muscle{transition:transform .5s ease, fill .4s; transform-origin:60px 46px; fill:rgba(255,255,255,.35)}
.rmef .pf-fast .pf-muscle{transition:transform .3s ease, fill .2s}
.rmef .pf-contract .pf-muscle{transform:translateY(-11px) scaleY(.55); fill:var(--contract)}
.rmef .pf-relax .pf-muscle{transform:translateY(7px) scaleY(1.12); fill:var(--relax)}
.rmef .pf-bone{fill:#e8f3ef}
.rmef .pf-arrow{opacity:0; transition:opacity .3s; fill:none; stroke:#fff; stroke-width:2.5; stroke-linecap:round}
.rmef .pf-contract .pf-arrow.up{opacity:.85}
.rmef .pf-relax .pf-arrow.down{opacity:.85}
.rmef .pf-label{font-size:.6rem; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:.5px}

.rmef .breath-cue{display:flex; align-items:center; justify-content:center; gap:8px; font-size:.88rem; font-weight:600;
  background:var(--card); padding:8px 16px; border-radius:999px; margin-bottom:10px;
  min-height:36px; color:var(--muted); transition:color .3s; text-align:center; width:100%}
.rmef .breath-cue.active{color:var(--text)}
.rmef .breath-cue.warn{color:#ffce54}

.rmef .progress-row{display:flex; gap:20px; margin-bottom:12px; font-size:.8rem; color:var(--muted); min-height:2.4em}
.rmef .progress-row b{color:var(--text); font-size:1rem; font-variant-numeric:tabular-nums}
.rmef .progress-row div{text-align:center}

.rmef .controls{display:flex; gap:12px; width:100%}
.rmef .btn{flex:1; padding:14px; border:none; border-radius:16px; font-size:1.02rem; font-weight:700; cursor:pointer; transition:transform .1s, opacity .2s; min-height:44px}
.rmef .btn:active{transform:scale(.97)}
.rmef .btn-primary{background:linear-gradient(135deg,var(--mefir),var(--mefir2)); color:#04211a; box-shadow:0 4px 18px rgba(31,173,144,.4)}
.rmef .btn-secondary{background:var(--card); color:var(--text)}
.rmef .btn[disabled]{opacity:.4; pointer-events:none}

.rmef .options{display:flex; gap:14px; margin-top:12px; font-size:.78rem; color:var(--muted); flex-wrap:wrap; justify-content:center}
.rmef .options label{display:flex; align-items:center; gap:5px; cursor:pointer; min-height:44px}
.rmef .options input{accent-color:var(--mefir); width:18px; height:18px}
.rmef #testVoice{margin-top:10px;padding:11px 18px;border:none;border-radius:12px;background:var(--card);color:var(--text);font-size:.82rem;font-weight:600;cursor:pointer;min-height:44px}
.rmef #voiceMsg{font-size:.68rem;color:var(--muted);margin-top:6px;min-height:1em;text-align:center;max-width:340px}

.rmef .done-msg{text-align:center; margin-top:8px; font-size:1rem; font-weight:700; color:var(--mefir2); animation:rmefpop .5s ease}
@keyframes rmefpop{0%{transform:scale(.7);opacity:0}100%{transform:scale(1);opacity:1}}

.rmef footer{margin-top:auto; padding-top:14px; font-size:.63rem; color:var(--muted); text-align:center; opacity:.8; max-width:360px; line-height:1.5}
.rmef .pd-mefir-logo{width:120px; height:auto; opacity:.85; margin-bottom:8px}
.rmef footer .brand{color:var(--mefir2); font-weight:700; letter-spacing:1px}

/* ---------- JACOBSON: sección nueva + silueta ---------- */
.rmef #home .lead-seccion{margin-top:18px}
.rmef .jfig-wrap{display:flex; flex-direction:column; align-items:center; gap:2px; width:min(24vw,92px); flex:none}
.rmef .jfig{width:100%; height:auto}
.rmef .jfig .reg{fill:rgba(255,255,255,.14); transition:fill .4s, filter .4s}
.rmef .jfig .reg.tense{fill:#f05252; filter:drop-shadow(0 0 5px rgba(240,82,82,.85))}
.rmef .jfig .reg.relax{fill:#4da3ff; filter:drop-shadow(0 0 5px rgba(77,163,255,.75))}
.rmef .jfig-label{font-size:.6rem; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:.5px}
.rmef .backbar{justify-content:space-between}

/* ---------- RESPIRACIÓN CUADRADA: cuadrado guía + punto ---------- */
.rmef .boxguide{position:absolute; inset:-13%; border:2px solid rgba(255,255,255,.12); border-radius:22px; pointer-events:none}
.rmef .boxdot{position:absolute; width:16px; height:16px; border-radius:50%; background:var(--mefir2);
  box-shadow:0 0 16px rgba(0,201,157,.8); transform:translate(-50%,-50%);
  transition:top .95s linear, left .95s linear, background .4s; top:6%; left:6%}
.rmef .boxdot.r{background:#4da3ff; box-shadow:0 0 16px rgba(77,163,255,.8)}
.rmef .boxdot.h{background:#ffce54; box-shadow:0 0 16px rgba(255,206,84,.8)}
.rmef .boxdot.g{background:var(--mefir2); box-shadow:0 0 16px rgba(0,201,157,.8)}
`;
